import React, { useState, useEffect, useRef } from 'react';
import { Alert, View, TouchableOpacity, Text, StyleSheet, Image, Animated, Platform, TextInput } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../src/utils/formatNumber';
import { localeFor } from '../../src/utils/dateLocale';
import { cookFacingError } from '../../src/i18n/cookFacingError';
import { LinearGradient } from 'expo-linear-gradient';
import Feather from '@expo/vector-icons/Feather';
import { EditableDetectedItem } from '../../src/types/foodRecognition';
import { CaptureReview } from '../../src/components/pantry/CaptureReview';
import { setPendingPantryToast } from '../../src/components/pantry/pendingToast';
import { FoodRecognitionResponse, ReceiptMetadata } from '../../src/types/foodRecognition';
import { formatLocalDateISO, parseLocalDateISO } from '../../src/utils/receiptProcessor';
import { PantryLocation, PANTRY_LOCATIONS } from '../../src/utils/pantryLocation';
import { ZONE_META } from '../../src/components/pantry/zoneMeta';
import { addItems, addItemsAndSync } from '../../src/redux/inventorySlice';
import { markPantryUpdated } from '../../src/redux/pantrySyncSlice';
import { learnZone, savePantryLearning } from '../../src/redux/pantryLearningSlice';
import { primaryKey } from '../../src/utils/pantryDuplicates';
import { trackEvent, captureError } from '../../src/services/analyticsService';
import { Paprika, PaprikaTypography, PaprikaSmartInventory, PaprikaRadius, PaprikaFreshness } from '../../src/styles/theme';
import aiService, { isQuotaExceededError, isNetworkFailure, isAuthRequiredError } from '../../src/services/aiService';
import { shouldResetForNewImage } from '../../src/components/inventory/captureSession';
import { RootState, AppDispatch } from '../../src/redux/store';
import { selectActiveInventory } from '../../src/redux/inventorySlice';
import { convertImageToBase64 } from '../../src/utils/imageProcessor';
import { buildScanReview, ScanReviewInputRow } from '../../src/utils/receiptScanReview';
import { newScanId, recordScanned, recordReviewed } from '../../src/services/receiptScanRecorder';
import { getCurrentLanguage } from '../../src/i18n';
import { RequireAccountToScan } from '../../src/components/auth/ScanSignUp';
import { authHrefReturningTo } from '../../src/utils/postAuthReturn';

/** The creep never passes this — only a real result may show 100%. */
const PROGRESS_CREEP_CEILING = 0.95;

const RecognitionResultsScreen: React.FC = () => {
  const router = useRouter();
  const params = useLocalSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const inventory = useSelector(selectActiveInventory);
  const { user, isGuest } = useSelector((state: RootState) => state.auth);
  const householdId = useSelector((state: RootState) => state.auth?.householdId ?? null);
  // What the user has already taught the pantry. The capture review's zone chip
  // has to show what the reducer will really do, and the reducer reads these first.
  const learnedZones = useSelector((state: RootState) => state.pantryLearning?.zones);
  const messageIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Tracks the imageUri we last analyzed. This screen is a hidden tab screen
  // (app/(app)/_layout.tsx) that stays MOUNTED between visits, so a second
  // capture arrives with a new imageUri while stale `recognitionResult`/`loading`
  // linger from the first analysis. We key a reset off imageUri changes (issue #488).
  const prevImageUriRef = useRef<string | null>(null);
  // The record of the scan on screen (#750): its id, and the write that has to
  // land before the cook's review can be attached to it.
  const scanRecordRef = useRef<{ scanId: string; scanned: Promise<boolean> } | null>(null);

  const imageUri = params.imageUri as string;
  const isProcessing = params.isProcessing === 'true';
  const analysisMode = (params.analysisMode as 'fridge' | 'receipt') || 'fridge';
  const zone = (params.zone as PantryLocation) || 'fridge';

  const [recognitionResult, setRecognitionResult] = useState<FoodRecognitionResponse | null>(null);
  const [loading, setLoading] = useState(isProcessing);
  const [error, setError] = useState<string | null>(null);
  // Retrying a refused scan is not free — the daily allowance is already spent,
  // so another attempt just spends more of a ceiling that has none left (#692).
  const [errorRetryable, setErrorRetryable] = useState(true);
  // The server saw no session: offer to sign back in instead of Retry.
  const [authRequired, setAuthRequired] = useState(false);

  // --- Purchase-date confirmation (receipt mode) ---
  // When the AI couldn't read a reliable date, we require the user to confirm one before saving
  // rather than silently storing a wrong/assumed date.
  const [editedDate, setEditedDate] = useState<string>(formatLocalDateISO(new Date()));
  const [dateConfirmed, setDateConfirmed] = useState(false);

  const receiptMeta = recognitionResult?.receipt_metadata;
  const needsDateConfirmation =
    analysisMode === 'receipt' &&
    !!receiptMeta &&
    (!receiptMeta.purchase_date || receiptMeta.purchase_date_needs_review === true);

  const isValidConfirmDate = (value: string): boolean => {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return false;
    const y = parseInt(m[1], 10), mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return false;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return dt.getTime() <= startOfToday.getTime(); // no future dates
  };

  // Initialise the editable date whenever results arrive. A valid AI date needs no confirmation;
  // a missing/flagged one defaults to today and must be confirmed by the user.
  useEffect(() => {
    const meta = recognitionResult?.receipt_metadata;
    if (!meta) return;
    if (meta.purchase_date && !meta.purchase_date_needs_review) {
      setEditedDate(meta.purchase_date);
      setDateConfirmed(true);
    } else {
      setEditedDate(formatLocalDateISO(new Date()));
      setDateConfirmed(false);
    }
  }, [recognitionResult]);
  const getCarouselMessages = () => [
    t('inventory.scanCarousel1'),
    t('inventory.scanCarousel2'),
    t('inventory.scanCarousel3'),
    t('inventory.scanCarousel4'),
    t('inventory.scanCarousel5'),
    t('inventory.scanCarousel6'),
    t('inventory.scanCarousel7'),
  ];

  const [currentLoadingMessage, setCurrentLoadingMessage] = useState('');
  // 0..1 completion. Fed by real milestones from aiService, nudged between
  // them by a creep that is capped short of 1 (see below).
  const [progressPct, setProgressPct] = useState(0);

  // Animated progress bar
  const progressAnim = useRef(new Animated.Value(0)).current;

  const startLoadingMessages = () => {
    const messages = getCarouselMessages();
    setCurrentLoadingMessage(messages[0]);

    let idx = 0;
    messageIntervalRef.current = setInterval(() => {
      idx = (idx + 1) % messages.length;
      setCurrentLoadingMessage(messages[idx]);
    }, 4000);
  };

  const stopLoadingMessages = () => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      stopLoadingMessages();
    };
  }, []);

  // A fresh capture re-enters this still-mounted hidden tab screen with a new
  // imageUri. Clear the previous analysis (otherwise the `!recognitionResult`
  // guard below blocks re-analysis and the user sees stale results) and reset
  // loading from the incoming params (issue #488). We intentionally do NOT
  // revoke blob URLs — the analyzer still needs the image, and premature
  // revocation caused ERR_FILE_NOT_FOUND. Blobs are released by the browser on
  // navigation/reload; the tiny leak is acceptable.
  useEffect(() => {
    if (!shouldResetForNewImage(prevImageUriRef.current, imageUri)) return;
    prevImageUriRef.current = imageUri;
    scanRecordRef.current = null;
    setRecognitionResult(null);
    setError(null);
    setAuthRequired(false);
    setLoading(isProcessing);
  }, [imageUri, isProcessing]);

  // Pre-computed results (deep links, QA harnesses) parse AFTER the reset
  // above. Declaration order is load-bearing: both effects fire on the first
  // mount, and when this one ran first the reset wiped the result it had just
  // parsed — the screen rendered null whenever results arrived by param.
  useEffect(() => {
    if (params.recognitionResult && !isProcessing) {
      try {
        const result = JSON.parse(params.recognitionResult as string) as FoodRecognitionResponse;
        setRecognitionResult(result);
        console.log('RecognitionResultsScreen - Using pre-computed results:', result.detected_items.length, 'items');
      } catch (error) {
        console.error('RecognitionResultsScreen - Error parsing results:', error);
        setError(t('inventory.parseFailed'));
      }
    }
  }, [params.recognitionResult]);

  // Progress creep between real milestones.
  //
  // This used to be the ONLY source of progress: five steps, 2.5s apart, so the
  // bar reached "100% COMPLETE" after 12.5s no matter how long the scan really
  // took — it read as finished while work continued. Now aiService reports true
  // milestones (image prepared, each band returned) and this only inches
  // forward between them, capped below 100% so it can never claim to be done.
  useEffect(() => {
    if (!loading) return;
    const creep = setInterval(() => {
      setProgressPct(prev => Math.min(prev + 0.01, PROGRESS_CREEP_CEILING));
    }, 1000);
    return () => clearInterval(creep);
  }, [loading]);

  // Drive the bar from whatever the current fraction is, real or crept.
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: progressPct,
      duration: 400,
      useNativeDriver: false,
    }).start();
  }, [progressPct]);

  useEffect(() => {
    if (isProcessing && imageUri && !recognitionResult) {
      startLoadingMessages();
      analyzeImage();
    }
  }, [isProcessing, imageUri, recognitionResult]);

  const analyzeImage = async () => {
    try {
      setError(null);
      setErrorRetryable(true);
      setAuthRequired(false);

      const onProgress = (message: string, progress?: number) => {
        // The service's message is English and stays in the log: the bar takes
        // the progress, the translated carousel keeps the words.
        console.log('Progress:', message, progress ?? '');
        // Only ever move forward: a late band must not rewind the bar.
        if (typeof progress === 'number') {
          setProgressPct(prev => Math.max(prev, progress));
        }
      };

      const result = await aiService.analyzeFoodImage(imageUri, onProgress, analysisMode);

      const displayStart = performance.now();
      setProgressPct(1); // the only place that may claim completion
      setRecognitionResult(result);

      // Keep what was read, now rather than on confirm: the cook in #750 shook
      // the phone on the review screen, and by then nothing was left to look at.
      // Signed-in only (the record lives under a uid), and never awaited.
      if (analysisMode === 'receipt' && !isGuest && user?.uid) {
        const scanId = newScanId();
        const photo = convertImageToBase64(imageUri, {
          maxWidth: 1000,
          quality: 0.6,
          preserveAspectRatio: true,
        }).then(({ base64, mimeType }) => ({ base64, mimeType }));
        scanRecordRef.current = {
          scanId,
          scanned: recordScanned(scanId, result, { language: getCurrentLanguage(), photo }),
        };
      }
      setTimeout(() => {
        const displayEnd = performance.now();
        console.log(`\u23F1\uFE0F [STEP 4] Results displayed: ${(displayEnd - displayStart).toFixed(0)}ms`);
      }, 100);

    } catch (err) {
      // The message, not the Error: an Error's `message` is non-enumerable, so
      // the bug reporter's console buffer serialized this as `{}` and #692
      // arrived with the real reason invisible.
      console.error(
        'RecognitionResultsScreen - Analysis error:',
        err instanceof Error ? err.message : String(err),
      );

      const outOfAllowance = isQuotaExceededError(err);
      const offline = isNetworkFailure(err);
      // Signed in on this device but the server saw no session (#775): not a
      // fault to retry or to file, a sentence with a way to sign back in.
      const signedOut = isAuthRequiredError(err);
      setAuthRequired(signedOut);
      // The server writes its refusals in English for tooling, never for a cook
      // — so translate the ones we recognise and only fall back to its prose.
      const errorMessage = signedOut
        ? t('scanGate.signedOut')
        : outOfAllowance
        ? t('inventory.quotaExceeded')
        : offline
          ? t('errors.networkError')
          : cookFacingError(err, t, 'errors.unknownError');

      setError(errorMessage);
      setErrorRetryable(!outOfAllowance && !signedOut);

      trackEvent('pantry_scan_failed', {
        mode: analysisMode,
        reason: signedOut ? 'auth-required' : outOfAllowance ? 'quota-exceeded' : offline ? 'network' : 'error',
      });
      // Without this a failed scan was invisible in PostHog — #692 only surfaced
      // because the cook shook the phone.
      if (!outOfAllowance && !offline && !signedOut && err instanceof Error) {
        captureError(err, { screen: 'recognition-results', mode: analysisMode });
      }

      Alert.alert(
        t('inventory.failedToAnalyze'),
        errorMessage,
        [
          ...(outOfAllowance || signedOut ? [] : [{ text: t('common.retry'), onPress: analyzeImage }]),
          { text: t('common.back'), onPress: () => router.navigate('/(app)/inventory') }
        ]
      );
    } finally {
      stopLoadingMessages();
      setLoading(false);
    }
  };

  if (!imageUri) {
    console.error('RecognitionResultsScreen - No image URI provided');
    Alert.alert(t('common.error'), t('smartInventory.noImage'), [
      { text: t('common.ok'), onPress: () => router.navigate('/(app)/inventory') }
    ]);
    return null;
  }

  const handleSave = async (acceptedItems: EditableDetectedItem[], reviewedItems?: EditableDetectedItem[]) => {
    console.log('RecognitionResultsScreen - Saving items to inventory:', acceptedItems.length);

    // Block saving until the user confirms the purchase date when the AI couldn't read a reliable one.
    if (needsDateConfirmation && !dateConfirmed) {
      Alert.alert(
        t('inventory.confirmDateTitle', 'Confirm purchase date'),
        t('inventory.confirmDateMessage', "We couldn't read the date on this receipt. Please confirm when you shopped before saving.")
      );
      return;
    }

    // The rows the cook removed are the scan's mistakes. Counted for everyone;
    // kept row by row, next to the band reads, for a signed-in cook.
    if (analysisMode === 'receipt' && reviewedItems) {
      const review = buildScanReview(reviewedItems as ScanReviewInputRow[]);
      trackEvent('receipt_scan_reviewed', {
        ...review.summary,
        store: recognitionResult?.receipt_metadata?.store_name ?? null,
        seam_merges: recognitionResult?.scan_evidence?.seam_merges ?? null,
        recorded: !!scanRecordRef.current,
      });
      const record = scanRecordRef.current;
      if (record) void recordReviewed(record.scanId, review, record.scanned);
    }

    // Track analytics for capture confirmation
    if (analysisMode === 'receipt') {
      const duplicateCount = acceptedItems.filter(item => (item as any).isDuplicate).length;
      const addedCount = acceptedItems.length - duplicateCount;
      trackEvent('pantry_capture_confirmed', {
        mode: 'receipt',
        added: addedCount,
        dups: duplicateCount
      });
    } else {
      trackEvent('pantry_capture_confirmed', {
        mode: 'fridge',
        zone,
        added: acceptedItems.length,
        dups: 0
      });
    }

    // A zone the user picked by hand is a fact about their kitchen; the app's
    // own guess is not. Learning the guess too would freeze today's keyword
    // rules into `pantryLearning.zones`, where no future fix to them can reach.
    const corrected = acceptedItems.filter(
      (item) => (item as { locationOverridden?: boolean }).locationOverridden && item.location
    );
    if (corrected.length > 0) {
      for (const item of corrected) {
        dispatch(learnZone({ key: primaryKey(item.name), zone: item.location! }));
      }
      dispatch(savePantryLearning({ userId: user?.uid, householdId }));
      trackEvent('pantry_capture_zone_corrected', {
        mode: analysisMode,
        corrected: corrected.length,
        of: acceptedItems.length,
      });
    }

    // Use the confirmed/edited date and clear the review flag before persisting.
    const receiptMetadata: ReceiptMetadata | undefined = recognitionResult?.receipt_metadata
      ? { ...recognitionResult.receipt_metadata, purchase_date: editedDate, purchase_date_needs_review: false }
      : undefined;

    if (!isGuest && user?.uid) {
      await dispatch(addItemsAndSync({
        items: acceptedItems,
        imageUri,
        receiptMetadata,
        userId: user.uid
      }));
    } else {
      dispatch(addItems({
        items: acceptedItems,
        imageUri,
        receiptMetadata
      }));
    }

    // Mark pantry as updated for sync prompt
    dispatch(markPantryUpdated());

    // Build and set pending toast message
    if (analysisMode === 'fridge') {
      // Photo mode: show zone-specific message
      const zoneName = t(ZONE_META[zone].labelKey);
      const toastMsg = t(`pantry.add.addedCountZone_${acceptedItems.length === 1 ? 'one' : 'other'}`, {
        count: acceptedItems.length,
        zone: zoneName,
      });
      setPendingPantryToast(toastMsg);
    } else {
      // Receipt mode: check for duplicates
      const duplicateCount = acceptedItems.filter(item => (item as any).isDuplicate).length;
      const addedCount = acceptedItems.length - duplicateCount;

      if (duplicateCount > 0) {
        const toastMsg = t('pantry.add.addedWithDups', { added: addedCount, dups: duplicateCount });
        setPendingPantryToast(toastMsg);
      } else {
        const toastMsg = t(`pantry.add.addedCount_${acceptedItems.length === 1 ? 'one' : 'other'}`, {
          count: acceptedItems.length,
        });
        setPendingPantryToast(toastMsg);
      }
    }

    console.log('RecognitionResultsScreen - Items saved, navigating back to pantry');
    router.navigate('/(app)/inventory');
  };

  const handleRetake = () => {
    console.log('RecognitionResultsScreen - User wants to retake photo');
    router.push({ pathname: '/(app)/inventory-camera', params: { analysisMode, zone } });
  };

  console.log('RecognitionResultsScreen - Rendering. Loading:', loading, 'Has Results:', !!recognitionResult);

  return (
    <View style={styles.container}>
      {/* === LOADING STATE — Editorial analyzing screen === */}
      {loading && (
        <View style={styles.analyzingContainer}>
          {/* Background: fridge image with warm overlay */}
          <View style={StyleSheet.absoluteFill}>
            <Image
              source={{ uri: imageUri }}
              style={styles.analyzingBgImage}
              resizeMode="cover"
            />
            <LinearGradient
              colors={['rgba(251,249,246,0.4)', 'rgba(245,243,240,0.9)']}
              style={StyleSheet.absoluteFill}
            />
          </View>

          {/* Glassmorphic Header */}
          <View style={styles.header}>
            <TouchableOpacity
              onPress={() => router.navigate('/(app)/inventory')}
              style={styles.headerButton}
            >
              <Text style={styles.headerButtonIcon}>{'\u2715'}</Text>
            </TouchableOpacity>
            <Text style={styles.headerTitleAccent}>{t('smartInventory.title')}</Text>
            <TouchableOpacity style={styles.headerButton}>
              <Text style={styles.headerButtonIcon}>{'\u22EE'}</Text>
            </TouchableOpacity>
          </View>

          {/* Content */}
          <View style={styles.analyzingContent}>
            {/* Hero title */}
            <View style={styles.heroSection}>
              <Text style={styles.heroTitle}>{t('smartInventory.curating')}</Text>
              <View style={styles.heroLine} />
            </View>

            {/* Rotating value prop message */}
            <View style={styles.valuePropSection}>
              <Text style={styles.valuePropText}>{currentLoadingMessage}</Text>
            </View>

            {/* Progress bar */}
            <View style={styles.progressSection}>
              <View style={styles.progressTrack}>
                <Animated.View
                  style={[
                    styles.progressFill,
                    {
                      width: progressAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0%', '100%'],
                      }),
                    },
                  ]}
                />
              </View>
              <Text style={styles.progressLabel}>
                {t('smartInventory.percentComplete', { percent: Math.round(progressPct * 100) })}
              </Text>
            </View>
          </View>
        </View>
      )}

      {/* === ERROR STATE === */}
      {!!error && !loading && (
        <View style={styles.errorContainer}>
          <View style={styles.header}>
            <TouchableOpacity
              onPress={() => router.navigate('/(app)/inventory')}
              style={styles.headerButton}
            >
              <Text style={styles.headerButtonIcon}>{'\u2715'}</Text>
            </TouchableOpacity>
            <Text style={styles.headerTitleAccent}>{t('smartInventory.title')}</Text>
            <View style={styles.headerButton} />
          </View>
          <View style={styles.errorContent}>
            <Text style={styles.errorText}>{t('inventory.failedToAnalyze', 'Failed to analyze image')}</Text>
            <Text style={styles.errorDetailText} testID="scan-error-detail">{error}</Text>
            {/* No Retry when the allowance is spent: the button cannot succeed,
                and pressing it is what turned one refusal into five (#692). */}
            {authRequired && (
              <TouchableOpacity
                style={styles.retryButton}
                onPress={() => router.push(authHrefReturningTo('/inventory') as any)}
                testID="scan-error-sign-in"
              >
                <Text style={styles.retryButtonText}>{t('scanGate.signInAgain')}</Text>
              </TouchableOpacity>
            )}
            {errorRetryable && (
              <TouchableOpacity style={styles.retryButton} onPress={analyzeImage} testID="scan-error-retry">
                <Text style={styles.retryButtonText}>{t('common.retry', 'Retry')}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}

      {/* === RESULTS STATE === */}
      {recognitionResult && !loading && (
        <>
          {/* Glassmorphic Header */}
          <View style={styles.header}>
            <TouchableOpacity
              onPress={() => router.navigate('/(app)/inventory')}
              style={styles.headerButton}
            >
              <Text style={styles.headerButtonIcon}>{'\u2715'}</Text>
            </TouchableOpacity>
            <Text style={styles.headerTitle}>{t('inventory.confirmItems')}</Text>
            <TouchableOpacity style={styles.headerButton}>
              <Text style={styles.headerButtonIcon}>{'\u22EE'}</Text>
            </TouchableOpacity>
          </View>

          {/* Incomplete-scan warning. Sits outside the receipt_metadata block on
              purpose: the band that failed may well be the one that carried the
              store name, so this has to show even with no metadata at all. */}
          {typeof recognitionResult.bands_ok === 'number' &&
            typeof recognitionResult.bands_total === 'number' &&
            recognitionResult.bands_ok < recognitionResult.bands_total && (
              <View style={styles.incompleteScanWarning}>
                <Feather name="alert-triangle" size={16} color={PaprikaFreshness.soon.text} />
                <Text style={styles.incompleteScanText}>
                  {t('inventory.incompleteScanWarning', {
                    failed: recognitionResult.bands_total - recognitionResult.bands_ok,
                    total: recognitionResult.bands_total,
                    defaultValue:
                      "We couldn't read {{failed}} of {{total}} sections of this receipt — some items may be missing. Add them by hand after saving.",
                  })}
                </Text>
              </View>
            )}

          {/* Receipt banner */}
          {recognitionResult.receipt_metadata && (
            <>
              <View style={styles.receiptBanner}>
                <View style={styles.receiptBannerRow}>
                  <Text style={styles.receiptBannerEmoji}>{'\uD83E\uDDFE'}</Text>
                  <View style={styles.receiptBannerInfo}>
                    <Text style={styles.receiptBannerStore}>
                      {recognitionResult.receipt_metadata.store_name || t('inventory.receipt')}
                    </Text>
                    <Text style={styles.receiptBannerMeta}>
                      {!!recognitionResult.receipt_metadata.purchase_date &&
                        parseLocalDateISO(recognitionResult.receipt_metadata.purchase_date)?.toLocaleDateString(localeFor(i18n.language))}
                      {!!recognitionResult.receipt_metadata.purchase_date && !!recognitionResult.receipt_metadata.total_amount && ' \u2022 '}
                      {!!recognitionResult.receipt_metadata.total_amount &&
                        formatMoney(recognitionResult.receipt_metadata.total_amount, i18n.language, recognitionResult.receipt_metadata.currency)}
                      {' \u2022 '}
                      {t('pantry.itemsCount', { count: recognitionResult.detected_items.length })}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Date confirmation gate — required when the AI couldn't read a reliable date */}
              {needsDateConfirmation && (
                <View style={styles.dateConfirmCard}>
                  <Text style={styles.dateConfirmTitle}>
                    {t('inventory.confirmDateTitle', 'Confirm purchase date')}
                  </Text>
                  <Text style={styles.dateConfirmSubtitle}>
                    {t('inventory.confirmDatePrompt', "We couldn't read the receipt date. When did you shop?")}
                  </Text>
                  <View style={styles.dateConfirmRow}>
                    <TextInput
                      style={styles.dateInput}
                      value={editedDate}
                      onChangeText={(text) => { setEditedDate(text); setDateConfirmed(false); }}
                      placeholder="YYYY-MM-DD"
                      placeholderTextColor={Paprika.textSecondary}
                      autoCapitalize="none"
                      keyboardType={Platform.OS === 'web' ? 'default' : 'numbers-and-punctuation'}
                    />
                    <TouchableOpacity
                      style={styles.quickDateButton}
                      onPress={() => { setEditedDate(formatLocalDateISO(new Date())); setDateConfirmed(false); }}
                    >
                      <Text style={styles.quickDateButtonText}>{t('inventory.today', 'Today')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.quickDateButton}
                      onPress={() => { setEditedDate(formatLocalDateISO(new Date(Date.now() - 86400000))); setDateConfirmed(false); }}
                    >
                      <Text style={styles.quickDateButtonText}>{t('inventory.yesterday', 'Yesterday')}</Text>
                    </TouchableOpacity>
                  </View>
                  <TouchableOpacity
                    style={[styles.confirmDateButton, dateConfirmed && styles.confirmDateButtonDone]}
                    onPress={() => {
                      if (!isValidConfirmDate(editedDate)) {
                        Alert.alert(
                          t('inventory.invalidDateTitle', 'Invalid date'),
                          t('inventory.invalidDateMessage', 'Please enter a valid date (YYYY-MM-DD) that is not in the future.')
                        );
                        return;
                      }
                      setDateConfirmed(true);
                    }}
                  >
                    <Text style={styles.confirmDateButtonText}>
                      {dateConfirmed
                        ? `✓ ${t('inventory.dateConfirmed', 'Date confirmed')}`
                        : t('inventory.confirmDateButton', 'Confirm date')}
                    </Text>
                  </TouchableOpacity>
                </View>
              )}

              {!needsDateConfirmation && !!recognitionResult.receipt_metadata.purchase_date && (() => {
                const purchaseDate = parseLocalDateISO(recognitionResult.receipt_metadata.purchase_date);
                if (!purchaseDate) return null;
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const daysOld = Math.round((today.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24));

                if (daysOld > 7) {
                  return (
                    <View style={styles.oldReceiptWarning}>
                      <Text style={styles.warningText}>
                        {t('smartInventory.oldReceipt', {
                          date: purchaseDate.toLocaleDateString(localeFor(i18n.language)),
                          count: daysOld,
                        })}
                      </Text>
                    </View>
                  );
                }
                return null;
              })()}
            </>
          )}

          <CaptureReview
            mode={analysisMode}
            zone={zone}
            result={recognitionResult}
            inventory={inventory}
            learnedZones={learnedZones}
            onConfirm={handleSave}
            onCancel={handleRetake}
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  // --- Header ---
  header: {
    ...PaprikaSmartInventory.glassmorphicHeader,
    paddingTop: Platform.OS === 'web' ? 16 : 50,
    zIndex: 50,
  },
  headerTitle: {
    ...PaprikaSmartInventory.headerTitle,
  },
  headerTitleAccent: {
    ...PaprikaSmartInventory.headerTitleAccent,
  },
  headerButton: {
    ...PaprikaSmartInventory.headerButton,
    width: 36,
  },
  headerButtonIcon: {
    fontSize: 20,
    color: Paprika.accent,
    fontWeight: '500',
  },
  // --- Analyzing state ---
  analyzingContainer: {
    flex: 1,
  },
  analyzingBgImage: {
    width: '100%',
    height: '100%',
    opacity: 0.6,
  },
  analyzingContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
    zIndex: 20,
  },
  heroSection: {
    alignItems: 'center',
    marginBottom: 48,
  },
  heroTitle: {
    ...PaprikaTypography.displayLarge,
    textAlign: 'center',
    color: Paprika.textPrimary,
    marginBottom: 16,
  },
  heroLine: {
    width: 64,
    height: 1,
    backgroundColor: Paprika.accent,
    opacity: 0.3,
  },
  valuePropSection: {
    marginBottom: 64,
    paddingHorizontal: 16,
    minHeight: 60,
    justifyContent: 'center',
  },
  valuePropText: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 18,
    lineHeight: 28,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  progressSection: {
    width: '100%',
    maxWidth: 280,
    alignItems: 'center',
    gap: 16,
  },
  progressTrack: {
    width: '100%',
    height: 2,
    backgroundColor: Paprika.surfaceHighest,
    borderRadius: 999,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: Paprika.accent,
    borderRadius: 999,
  },
  progressLabel: {
    fontSize: 11,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    letterSpacing: 3,
    textTransform: 'uppercase',
    color: Paprika.textSecondary,
    opacity: 0.7,
  },
  // --- Error state ---
  errorContainer: {
    flex: 1,
  },
  errorContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorText: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorDetailText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    paddingHorizontal: 16,
  },
  retryButton: {
    backgroundColor: Paprika.accent,
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: PaprikaRadius.full,
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
  },
  // --- Receipt banner ---
  receiptBanner: {
    backgroundColor: Paprika.surfaceLow,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  receiptBannerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  receiptBannerEmoji: {
    fontSize: 24,
    marginRight: 12,
  },
  receiptBannerInfo: {
    flex: 1,
  },
  receiptBannerStore: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    marginBottom: 2,
  },
  receiptBannerMeta: {
    ...PaprikaTypography.caption,
    color: Paprika.textSecondary,
  },
  // Partial-scan warning (#658). Uses the design system's "soon" warm-gold
  // pair rather than the bootstrap yellow the older warnings below hardcode.
  incompleteScanWarning: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: PaprikaFreshness.soon.bg,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  incompleteScanText: {
    flex: 1,
    ...PaprikaTypography.bodySmall,
    color: PaprikaFreshness.soon.text,
  },
  oldReceiptWarning: {
    backgroundColor: '#fff3cd',
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  warningText: {
    flex: 1,
    ...PaprikaTypography.bodySmall,
    color: '#856404',
  },
  // --- Purchase-date confirmation gate ---
  dateConfirmCard: {
    backgroundColor: '#fff3cd',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: '#ffe69c',
  },
  dateConfirmTitle: {
    ...PaprikaTypography.titleMedium,
    color: '#856404',
    marginBottom: 2,
  },
  dateConfirmSubtitle: {
    ...PaprikaTypography.bodySmall,
    color: '#856404',
    marginBottom: 10,
  },
  dateConfirmRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  dateInput: {
    flex: 1,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    backgroundColor: Paprika.surface,
    borderWidth: 1,
    borderColor: '#ffe69c',
    borderRadius: PaprikaRadius.sm,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginRight: 8,
  },
  quickDateButton: {
    backgroundColor: Paprika.surface,
    borderWidth: 1,
    borderColor: '#ffe69c',
    borderRadius: PaprikaRadius.sm,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginLeft: 4,
  },
  quickDateButtonText: {
    ...PaprikaTypography.bodySmall,
    color: '#856404',
  },
  confirmDateButton: {
    backgroundColor: Paprika.primary,
    borderRadius: PaprikaRadius.sm,
    paddingVertical: 12,
    alignItems: 'center',
  },
  confirmDateButtonDone: {
    backgroundColor: Paprika.success,
  },
  confirmDateButtonText: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.surface,
  },
});

// A guest arriving here would only ever get a 401 (#775): show the sign-up
// panel instead of mounting the screen, so no analysis is started.
function RecognitionResultsRoute() {
  const { analysisMode } = useLocalSearchParams<{ analysisMode?: string }>();
  return (
    <RequireAccountToScan
      kind={analysisMode === 'receipt' ? 'receipt' : 'fridge'}
      source="scan_results"
      returnTo="/inventory"
    >
      <RecognitionResultsScreen />
    </RequireAccountToScan>
  );
}

export default RecognitionResultsRoute;
